import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FlowService } from '../services/flow.service';
import { FlowStateService } from '../services/flow.state.service';
import { FlowModeSubject } from '../subject/flow.mode.subject';
import { PropertyInitialSubject } from '../subject/property.initial.subject';
import { SegmentSubjectService } from '../subject/segment.subject';
import { PropertySubject } from '../subject/property.subject';
import { FlowMode } from '../dto/enum/flow.mode.enum';

/**
 * Fachada del wizard de publicación. Unifica las piezas de flujo que cada
 * paso inyectaba por separado (FlowService + FlowStateService + FlowModeSubject
 * + Router) y centraliza el patrón repetido `setCurrentStep → getNext/Prev →
 * navegar`. Los pasos conservan su lógica de dominio (forms, HTTP, mapas, S3)
 * y delegan SOLO la navegación/estado de flujo aquí.
 *
 * Singleton (providedIn root) porque el estado del wizard (propertyId, stepData)
 * vive durante todo el flujo.
 */
@Injectable({ providedIn: 'root' })
export class PublishFlowFacade {

    constructor(
        private readonly flow: FlowService,
        private readonly state: FlowStateService,
        private readonly modeSubject: FlowModeSubject,
        private readonly router: Router,
        private readonly propertyInitial: PropertyInitialSubject,
        private readonly segmentSubject: SegmentSubjectService,
        private readonly propertySubject: PropertySubject,
    ) {}

    /**
     * Inicia una publicación NUEVA desde cero: limpia TODO el estado del wizard
     * (propertyId, etapa, datos por paso, segmento, nombre) y fija modo CREATE.
     * Se llama al entrar por "Crear propiedad". El RESUME (desde list-pending)
     * NO pasa por aquí: navega directo al paso correspondiente.
     */
    startNew(): void {
        this.state.clear();
        this.modeSubject.setMode(FlowMode.CREATE);
        this.propertyInitial.resetProperty();
        this.segmentSubject.clearSelectedSegment();
        this.propertySubject.clear();
    }

    /** Marca el paso actual del wizard (para resume/progreso). */
    initStep(step: string): void {
        this.state.setCurrentStep(step);
    }

    // ---- Modo (CREATE / EDIT) ----
    get mode(): FlowMode | null {
        return this.modeSubject.getCurrentMode();
    }

    setMode(mode: FlowMode): void {
        this.modeSubject.setMode(mode);
    }

    get isEditMode(): boolean {
        return this.mode === FlowMode.EDIT;
    }

    // ---- Propiedad en construcción ----
    get propertyId(): number | null {
        return this.state.propertyId;
    }

    setPropertyId(id: number): void {
        this.state.setPropertyId(id);
    }

    // ---- Data por paso ----
    getStepData<T = any>(step: string): T {
        return this.state.getStepData(step);
    }

    setStepData(step: string, data: any): void {
        this.state.setStepData(step, data);
    }

    // ---- Navegación ----
    nextUrl(step: string): string {
        return this.flow.getNextStep(step);
    }

    previousUrl(step: string): string {
        return this.flow.getPreviousStep(step);
    }

    next(step: string): void {
        this.router.navigate([this.flow.getNextStep(step)]);
    }

    previous(step: string): void {
        this.router.navigate([this.flow.getPreviousStep(step)]);
    }

    goTo(url: string): void {
        if (url) {
            this.router.navigate([url]);
        }
    }

    /** Guarda la data del paso (si se pasa) y avanza al siguiente. */
    saveAndNext(step: string, data?: any): void {
        if (data !== undefined) {
            this.state.setStepData(step, data);
        }
        this.next(step);
    }

    /** Progreso 0–100 según el índice del paso en el flujo. */
    progress(step: string): number {
        const total = this.flow.getAllSteps().length;
        const index = this.flow.getStepIndex(step);
        if (total <= 0 || index < 0) {
            return 0;
        }
        return Math.round(((index + 1) / total) * 100);
    }
}
